import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Loader2, Building2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { ESTADOS, TIPOS_NOMINA, fechaCorta, pesos, nombreSugerido, periodoPorTipo } from '../nomina/utils/nomina';
import { getSedesNomina } from '../nomina/Services/nomina.services';
import { getPersonalAdministrativo } from '../administrativo/Services/administrativo.services';
import {
  getNominasDirectas,
  crearNominaDirecta,
  METODOS_DISTRIBUCION,
} from './Services/nomina-directa.services';
import ParticipacionCentros from './components/ParticipacionCentros';

const hoyISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });

const formInicial = () => {
  const { desde, hasta } = periodoPorTipo('mensual', hoyISO());
  return {
    tipo: 'mensual',
    desde,
    hasta,
    nombre: `directa-${nombreSugerido(desde, hasta, 'mensual')}`,
    nombreEditado: false,
    metodoDistribucion: 'kilos',
    centros: [],
    todasLasPersonas: true,
    personas: [],
  };
};

const NominaDirectaListPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState('todas');
  const [sedes, setSedes] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(formInicial());
  const [creando, setCreando] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      setItems(await getNominasDirectas({ estado }));
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, [estado]);

  useEffect(() => {
    getSedesNomina().then(setSedes).catch(() => setSedes([]));
    getPersonalAdministrativo({ estado: 'Activo' }).then(setPersonal).catch(() => setPersonal([]));
  }, []);

  const actualizar = (cambios) => {
    setForm((f) => {
      let sig = { ...f, ...cambios };
      if (cambios.tipo && cambios.tipo !== 'otro') {
        sig = { ...sig, ...periodoPorTipo(cambios.tipo, f.desde || hoyISO()) };
      }
      if ((cambios.tipo || cambios.desde || cambios.hasta) && !sig.nombreEditado) {
        sig.nombre = `directa-${nombreSugerido(sig.desde, sig.hasta, sig.tipo)}`;
      }
      return sig;
    });
  };

  const alternar = (campo, item) =>
    setForm((f) => ({
      ...f,
      [campo]: f[campo].some((x) => x.id === item.id)
        ? f[campo].filter((x) => x.id !== item.id)
        : [...f[campo], item],
    }));

  const sumaPorcentajes = form.centros.reduce((s, c) => s + (Number(c.porcentaje) || 0), 0);

  const crear = async (e) => {
    e.preventDefault();
    if (!form.todasLasPersonas && form.personas.length === 0) {
      toast({ title: 'Personas', description: 'Elige al menos una persona o marca todas.', variant: 'destructive' });
      return;
    }
    if (form.metodoDistribucion === 'porcentaje' && form.centros.length) {
      if (Math.abs(sumaPorcentajes - 100) > 0.01) {
        toast({
          title: 'Porcentajes',
          description: `Los porcentajes suman ${sumaPorcentajes}. Deben sumar 100.`,
          variant: 'destructive',
        });
        return;
      }
    }
    setCreando(true);
    try {
      const nomina = await crearNominaDirecta({
        nombre: form.nombre.trim(),
        tipo: form.tipo,
        desde: form.desde,
        hasta: form.hasta,
        metodoDistribucion: form.metodoDistribucion,
        centros: form.centros.map((c) => ({
          id: c.id,
          nombre: c.nombre,
          porcentaje: form.metodoDistribucion === 'porcentaje' ? Number(c.porcentaje) || 0 : null,
        })),
        personas: form.todasLasPersonas ? [] : form.personas.map((p) => p.id),
        creadoPor: { id: user?._id || user?.id, nombre: user?.nombre || user?.name || user?.email },
      });
      toast({
        title: 'Nómina directa generada',
        description: `${nomina.totales?.personas ?? 0} personas repartidas en ${(nomina.centros || []).length} centros.`,
      });
      setOpen(false);
      navigate(`/personal/nomina-directa/${nomina._id}`);
    } catch (err) {
      toast({ title: 'No se generó', description: err.message, variant: 'destructive' });
    } finally {
      setCreando(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 p-2.5 rounded-lg">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Nómina directa</h2>
            <p className="text-sm text-muted-foreground">
              Liquida al personal que no marca en planta y reparte su costo entre los centros de lavado, para que
              entre en el costo por kilo de cada planta.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Estado</Label>
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                {Object.entries(ESTADOS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => { setForm(formInicial()); setOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" /> Nueva nómina directa
          </Button>
        </div>

        <div className="rounded-lg border bg-card overflow-x-auto">
          <Table className="[&_td]:px-2 [&_th]:px-2">
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Periodo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Reparto</TableHead>
                <TableHead className="text-right">Personas</TableHead>
                <TableHead className="text-right">Centros</TableHead>
                <TableHead className="text-right">Devengado</TableHead>
                <TableHead className="text-right">Neto</TableHead>
                <TableHead className="text-right" title="Devengado + aportes + prestaciones">Costo empresa</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">Cargando…</TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">
                    Todavía no hay nóminas directas. Crea la primera con «Nueva nómina directa».
                  </TableCell>
                </TableRow>
              ) : (
                items.map((n) => (
                  <TableRow
                    key={n._id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/personal/nomina-directa/${n._id}`)}
                  >
                    <TableCell className="font-medium whitespace-nowrap">{n.nombre}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {fechaCorta(n.desde)} – {fechaCorta(n.hasta)}
                      <div className="text-xs text-muted-foreground">{TIPOS_NOMINA[n.tipo] || n.tipo}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={ESTADOS[n.estado]?.variant}>{ESTADOS[n.estado]?.label || n.estado}</Badge>
                    </TableCell>
                    <TableCell
                      className="whitespace-nowrap text-sm"
                      title={`Reparto por defecto: ${METODOS_DISTRIBUCION[n.metodoDistribucion] || n.metodoDistribucion}`}
                    >
                      Por persona
                      <div className="text-xs text-muted-foreground">
                        defecto: {METODOS_DISTRIBUCION[n.metodoDistribucion] || n.metodoDistribucion}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{n.totales?.personas ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums">{(n.centros || []).length}</TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(n.totales?.devengado)}</TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(n.totales?.neto)}</TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">
                      {pesos(n.totales?.costoEmpresa)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => !creando && setOpen(v)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva nómina directa</DialogTitle>
            <DialogDescription>
              Se liquida a cada persona desde su ficha (básico por los días del periodo, auxilio de transporte y
              deducciones de ley) y el costo se reparte entre los centros con el método que elijas.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={crear} className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Periodo</Label>
                <Select value={form.tipo} onValueChange={(v) => actualizar({ tipo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TIPOS_NOMINA).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nd-desde">Desde</Label>
                <Input id="nd-desde" type="date" value={form.desde} onChange={(e) => actualizar({ desde: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nd-hasta">Hasta</Label>
                <Input id="nd-hasta" type="date" min={form.desde} value={form.hasta} onChange={(e) => actualizar({ hasta: e.target.value })} required />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nd-nombre">Nombre</Label>
              <Input
                id="nd-nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value, nombreEditado: true })}
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4" /> Personas
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.todasLasPersonas}
                  onCheckedChange={(v) => setForm({ ...form, todasLasPersonas: !!v })}
                />
                Todo el personal administrativo activo ({personal.length})
              </label>
              {!form.todasLasPersonas && (
                <div className="max-h-40 overflow-y-auto rounded-md border p-2 grid gap-1 md:grid-cols-2">
                  {personal.length === 0 && (
                    <p className="md:col-span-2 text-sm text-muted-foreground">
                      No hay personal administrativo activo. Regístralo primero.
                    </p>
                  )}
                  {personal.map((p) => (
                    <label key={p._id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={form.personas.some((x) => x.id === p._id)}
                        onCheckedChange={() => alternar('personas', { id: p._id, nombre: p.nombre })}
                      />
                      <span className="truncate">{p.nombre} {p.apellido}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Building2 className="h-4 w-4" /> Reparto por defecto
              </div>
              <p className="text-xs text-muted-foreground">
                Cada persona se reparte con la participación de su ficha. Esto se usa solo para quienes no la
                tengan configurada. Sin centros elegidos, se reparte entre todos los activos.
              </p>
              <ParticipacionCentros
                metodo={form.metodoDistribucion}
                centros={form.centros}
                sedes={sedes}
                onChange={({ metodo, centros }) => setForm((f) => ({ ...f, metodoDistribucion: metodo, centros }))}
                ayudaVacio="Sin centros elegidos: el reparto por defecto toma todos los centros activos."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={creando}>
                Cancelar
              </Button>
              <Button type="submit" disabled={creando}>
                {creando ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generando…
                  </>
                ) : (
                  'Generar nómina directa'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default NominaDirectaListPage;
